import { render } from 'preact/compat';
import * as tinymce from '@ts/helpers/tinymce';
import { CommentFormFooter } from './CommentFormFooter';
import {
  GetReplyProps,
  LoadReplyProps,
  ReplyData,
  SetFooterProps,
} from './formTypes';

const style = (id: string) => {
  // Initialize TinyMCE
  tinymce.prepareEditor(`#${id}`, tinymce.config.comments);
};

const setFooter = ({
  confirmButton,
  stateUpdater,
  commentId,
  replyForm,
}: SetFooterProps) => {
  // Only reply comments have `commentId`
  const id = commentId
    ? `reply-comment-text-${commentId}`
    : 'form-widgets-comment-text';

  style(id);

  const modalButtonPlaceholder = document.createElement('div');

  render(
    <CommentFormFooter
      id={id}
      commentId={commentId}
      replyForm={replyForm}
      stateUpdater={stateUpdater}
      confirmButton={confirmButton}
    />,
    modalButtonPlaceholder,
  );

  const formFooterElement =
    modalButtonPlaceholder.firstElementChild as HTMLDivElement;

  const modalButton = formFooterElement.getElementsByTagName('button')[0];
  if (!modalButton) return;

  confirmButton?.after(formFooterElement);
};

const getReply = async ({ service, urlParams, commentId }: GetReplyProps) => {
  try {
    // Fetch reply form within the service layer
    const replyFormHtmlStr = await service.getReplyForm(urlParams);

    if (!replyFormHtmlStr.includes(commentId))
      throw new Error('Error loading the reply form');

    // Parse the HTML string into the document object
    const parser = new DOMParser();
    const doc = parser.parseFromString(replyFormHtmlStr, 'text/html');
    const replyForm =
      doc.querySelector<HTMLElement>('#reply-to-comment-form') || undefined;

    return replyForm;
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting comments: ${error.message}`);

      // Show message to the user
      const loading = document.querySelector('.js-loading');
      if (!loading) return;

      loading.textContent = 'Das Formular konnte nicht geladen werden.';
    }
  }
};

const loadReply = async ({
  target,
  replyButton,
  service,
}: LoadReplyProps): Promise<ReplyData | undefined> => {
  // Add a placeholder to put the reply form in
  const replyForm = document.createElement('div');
  replyForm.classList.add('reply-form');

  target.parentElement?.after(replyForm);

  // Give quick user feedback that the reply form is being loaded
  document.querySelector('.js-loading')?.remove();

  const loading = document.createElement('p');
  loading.classList.add('bc-comment-form__loading', 'js-loading');
  loading.innerText = 'Das Formular lädt...';

  target.after(loading);

  // Get url params from the target's href attr
  const urlParams = target.getAttribute('href')?.split('?')[1];
  if (!urlParams) return;

  const commentId = replyButton.closest('.js-comment-container')?.children[0]
    ?.id;
  if (!commentId) return;

  const form = await getReply({ service, urlParams, commentId });
  if (!form) return;

  // The form was successfully loaded, there is no need to show the button
  target.style.display = 'none';
  loading.remove();

  replyForm.innerHTML = form.outerHTML;

  document
    .querySelector(`#reply-comment-text-${commentId}`)
    ?.removeAttribute('required');

  // Style and clean up replyForm
  // Cleanup, style and text fixes
  replyForm.classList.add('bc-comment-form');
  replyForm.querySelector('#form-buttons-cancel')?.remove();

  const label = replyForm.getElementsByTagName('label')[0];
  if (!label) return;
  label.textContent = 'Antworten';

  // Get confirm comment button
  const confirmButton = replyForm.querySelector<HTMLElement>('.submit-widget');
  if (!confirmButton) return;

  confirmButton.setAttribute('value', 'Antworten');

  return { confirmButton, commentId, replyForm };
};

export const commentForm = {
  setFooter,
  loadReply,
};
